import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, RefreshCw, Clock, Shield, FileText, Calendar, Download, Database, HardDrive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupStats, setBackupStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchSettings();
    fetchLogs();
    fetchBackupStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeaders(),
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Баптауларды жүктеу кезінде қате орын алды');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/logs?limit=50`, {
        headers: getAuthHeaders(),
      });
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchBackupStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/stats`, {
        headers: getAuthHeaders(),
      });
      setBackupStats(response.data);
    } catch (error) {
      console.error('Error fetching backup stats:', error);
    }
  };

  const downloadBackup = async (format) => {
    setDownloading(true);
    try {
      const endpoint = format === 'json' ? '/admin/backup/export' : '/admin/backup/export-excel';
      const response = await axios.get(`${API}${endpoint}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'json' ? 'json' : 'xlsx';
      link.setAttribute('download', `merit_plus_backup_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Сақтық көшірме жүктелді');
    } catch (error) {
      toast.error('Жүктеу кезінде қате орын алды');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      setSettings(response.data);
      toast.success('Баптаулар сақталды');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Баптауларды сақтау кезінде қате орын алды');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
        <p className="text-muted">Жүктелуде...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold heading-font tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8 text-primary" />
          Жүйе баптаулары
        </h2>
        <p className="text-muted">
          Жүйені басқару және баптау
        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Жалпы
          </TabsTrigger>
          <TabsTrigger value="backup">
            <HardDrive className="w-4 h-4 mr-2" />
            Сақтық көшірме
          </TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="w-4 h-4 mr-2" />
            Қауіпсіздік
          </TabsTrigger>
          <TabsTrigger value="logs">
            <FileText className="w-4 h-4 mr-2" />
            Журналдар
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Ай сайынғы баптаулар
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Monthly Reset Day */}
              <div className="space-y-2">
                <Label htmlFor="reset-day">Ай сайын ұпайды қайта бастау күні (1-31)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="reset-day"
                    type="number"
                    min="1"
                    max="31"
                    value={settings?.monthly_reset_day || 1}
                    onChange={(e) => setSettings({
                      ...settings,
                      monthly_reset_day: parseInt(e.target.value)
                    })}
                    className="w-32"
                  />
                  <p className="text-sm text-muted">
                    Қазіргі: Ай сайын <strong>{settings?.monthly_reset_day}</strong>-ші күні
                  </p>
                </div>
              </div>

              {/* Auto Archive */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Автоматты мұрағаттау</Label>
                  <p className="text-sm text-muted">
                    Ай сайын автоматты түрде мұрағаттау және ұпайларды қайта бастау
                  </p>
                </div>
                <Switch
                  checked={settings?.auto_archive_enabled || false}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    auto_archive_enabled: checked
                  })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Settings */}
        <TabsContent value="backup" className="space-y-6">
          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                Деректер қоры статистикасы
              </CardTitle>
            </CardHeader>
            <CardContent>
              {backupStats ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{backupStats.students}</p>
                    <p className="text-sm text-muted">Оқушылар</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{backupStats.teachers}</p>
                    <p className="text-sm text-muted">Мұғалімдер</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{backupStats.classes}</p>
                    <p className="text-sm text-muted">Сыныптар</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-primary">{backupStats.records}</p>
                    <p className="text-sm text-muted">Жазбалар</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">Жүктелуде...</div>
              )}
            </CardContent>
          </Card>

          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                Сақтық көшірме жүктеу
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted">
                Барлық деректерді жүктеп алыңыз. Бұл файлды қауіпсіз жерде сақтаңыз.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => downloadBackup('json')}
                  disabled={downloading}
                  className="flex-1 min-w-[200px]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Жүктелуде...' : 'JSON форматында жүктеу'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => downloadBackup('excel')}
                  disabled={downloading}
                  className="flex-1 min-w-[200px]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Жүктелуде...' : 'Excel форматында жүктеу'}
                </Button>
              </div>

              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>💡 Кеңес:</strong> Апта сайын сақтық көшірме жасауды ұсынамыз. 
                  JSON форматы толық деректерді сақтайды, Excel форматы оңай қарау үшін ыңғайлы.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-6">
          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Қауіпсіздік баптаулары
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Min Password Length */}
              <div className="space-y-2">
                <Label htmlFor="min-password">Минималды құпия сөз ұзындығы</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="min-password"
                    type="number"
                    min="4"
                    max="16"
                    value={settings?.min_password_length || 4}
                    onChange={(e) => setSettings({
                      ...settings,
                      min_password_length: parseInt(e.target.value)
                    })}
                    className="w-32"
                  />
                  <p className="text-sm text-muted">
                    Қазіргі: Кемінде <strong>{settings?.min_password_length}</strong> таңба
                  </p>
                </div>
              </div>

              {/* Session Timeout */}
              <div className="space-y-2">
                <Label htmlFor="session-timeout">Сессия уақыты (минут)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="session-timeout"
                    type="number"
                    min="30"
                    max="10080"
                    step="30"
                    value={settings?.session_timeout_minutes || 1440}
                    onChange={(e) => setSettings({
                      ...settings,
                      session_timeout_minutes: parseInt(e.target.value)
                    })}
                    className="w-32"
                  />
                  <p className="text-sm text-muted">
                    Қазіргі: <strong>{settings?.session_timeout_minutes}</strong> минут
                    ({Math.round(settings?.session_timeout_minutes / 60)} сағат)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Logs */}
        <TabsContent value="logs" className="space-y-6">
          <Card className="paper-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Жүйе журналдары
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLogs}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Жаңарту
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-muted">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Журналдар жоқ</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{log.details}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(log.timestamp), 'dd.MM.yyyy HH:mm')}
                            </span>
                            <span>•</span>
                            <span>{log.user_name}</span>
                            <span>•</span>
                            <span className="capitalize">{log.action}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button - Fixed at bottom */}
      <div className="sticky bottom-0 bg-background pt-4 pb-4 border-t">
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={fetchSettings}
            disabled={saving}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Қайта жүктеу
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Сақталуда...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Баптауларды сақтау
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
